import React, { MouseEventHandler } from 'react';
import ReactMarkdown from 'react-markdown';
import { ButtonColor, ButtonIconPosition, ButtonSize, ButtonVariant, IconButton } from 'react-magma-dom';
import { AccountCircleIcon, ClearIcon, EditIcon, MenuIcon } from 'react-magma-icons';
import {
  StyledBurgerButton,
  StyledCloseModalButton,
  StyledCrossButton,
  StyledAddPhotoButton,
  StyledEditPhotoButton,
  StyledNavButton,
  StyledProformaButton,
  StyledModalButton,
} from '../Styled';
import { AddPicture } from '../../assets/icons/EditTool';
import { StyledFilledCrossButton, StyledHybridUnderlineControlButton } from '../Styled/StyledButtons';

interface IButton {
  text: string;
  icon: JSX.Element;
  onClick?: MouseEventHandler<HTMLButtonElement> | undefined;
  iconPosition?: ButtonIconPosition;
  id?: string;
  ariaLabel?: string;
  isActive?: boolean;
}

interface ICrossButton {
  visible: boolean;
  ariaLabel: string;
  top?: number;
  right?: number;
  width?: number;
  height?: number;
  testId?: string;
  onClick: MouseEventHandler<HTMLButtonElement> | undefined;
}

interface IFilledCrossButton {
  bgColor: string;
  onClick: MouseEventHandler<HTMLButtonElement> | undefined;
}

interface ICloseModalButton {
  ariaLabel: string;
  onClick: MouseEventHandler<HTMLButtonElement> | undefined;
}

interface IAccountButton {
  handleClick?: MouseEventHandler<HTMLButtonElement> | undefined;
}

interface IBurgerButton {
  handleClick?: MouseEventHandler<HTMLButtonElement> | undefined;
}

interface IProformaButton {
  isLFLabel?: boolean;
  bGColor: string;
  text: string;
  withBorder?: boolean;
  inverted?: boolean;
  withoutMargin?: boolean;
  onButtonClick: () => void;
  isDisabled?: boolean;
}

interface IModalButton {
  borderColor?: string;
  color: string;
  bGColor: string;
  activeColor?: string;
  text: string;
  margin?: string;
  onButtonClick: () => void;
  isDisabled?: boolean;
}

export const Button = (props: IButton): JSX.Element => {
  const { ariaLabel, text, icon, onClick, iconPosition, id, isActive } = props;

  return (
    <StyledNavButton
      testId="navigation-item"
      id={id}
      aria-label={ariaLabel}
      icon={icon}
      variant={isActive ? ButtonVariant.solid : ButtonVariant.link}
      iconPosition={iconPosition}
      onClick={onClick}
      tabIndex={0}
    >
      {text}
    </StyledNavButton>
  );
};

export const CrossButton: React.FC<ICrossButton> = (props): JSX.Element => {
  const { ariaLabel, visible, testId, top, right, width, height, onClick } = props;

  return (
    <>
      {visible ? (
        <StyledCrossButton
          testId={testId}
          variant={ButtonVariant.link}
          color={ButtonColor.secondary}
          top={top}
          right={right}
          width={width}
          height={height}
          aria-label={ariaLabel}
          icon={<ClearIcon />}
          onClick={onClick}
        />
      ) : null}
    </>
  );
};

export const FilledCrossButton: React.FC<IFilledCrossButton> = (props): JSX.Element => {
  const { bgColor, onClick } = props;

  return (
    <StyledFilledCrossButton
      icon={<ClearIcon />}
      color={ButtonColor.secondary}
      variant={ButtonVariant.link}
      bgColor={bgColor}
      aria-label="filled cross button"
      testId="filled-cross-button"
      onClick={onClick}
    />
  );
};

export const CloseModalButton: React.FC<ICloseModalButton> = (props): JSX.Element => {
  const { ariaLabel, onClick } = props;
  return (
    <StyledCloseModalButton
      testId="close-modal-btn"
      variant={ButtonVariant.link}
      color={ButtonColor.secondary}
      aria-label={ariaLabel}
      icon={<ClearIcon />}
      onClick={onClick}
      size={ButtonSize.small}
    />
  );
};

export const AccountButton: React.FC<IAccountButton> = (): JSX.Element => {
  return <IconButton size={ButtonSize.small} aria-label="Go to account page" icon={<AccountCircleIcon />} />;
};

export const BurgerMenuButton: React.FC<IBurgerButton> = ({ handleClick }): JSX.Element => {
  return (
    <StyledBurgerButton
      onClick={handleClick}
      variant={ButtonVariant.link}
      size={ButtonSize.small}
      aria-label="Open navigation bar"
      icon={<MenuIcon size={30} />}
    />
  );
};

export const ProformaButton: React.FC<IProformaButton> = (props): JSX.Element => {
  const { isLFLabel, bGColor, text, withBorder, inverted, withoutMargin, onButtonClick, isDisabled } = props;

  const handleClick = () => {
    if (isDisabled) {
      return;
    }
    onButtonClick();
  };

  return (
    <StyledProformaButton
      isLFLabel={isLFLabel}
      withBorder={withBorder}
      withoutMargin={withoutMargin}
      inverted={inverted}
      bGColor={bGColor}
      onClick={handleClick}
      disabled={isDisabled}
      data-testid={`proforma-language-${text}-button`}
    >
      <ReactMarkdown components={{ p: React.Fragment }} skipHtml={false}>
        {text}
      </ReactMarkdown>
    </StyledProformaButton>
  );
};

export const ModalButton: React.FC<IModalButton> = (props): JSX.Element => {
  const { borderColor, bGColor, text, color, margin, onButtonClick, isDisabled } = props;

  return (
    <StyledModalButton
      disabled={isDisabled as boolean}
      testId="modal-button"
      bGColor={bGColor}
      onClick={onButtonClick}
      borderColor={borderColor}
      textColor={color}
      margin={margin || '4px 16px 4px 4px'}
    >
      {text}
    </StyledModalButton>
  );
};

interface IPhotoButton {
  color: string;
  onClick: () => void;
}

export const AddPhotoButton: React.FC<IPhotoButton> = ({ color, onClick }): JSX.Element => {
  return (
    <StyledAddPhotoButton color={color} onClick={onClick} data-testid="add-photo-btn">
      <AddPicture color={color} />
      <span>Add a photo</span>
    </StyledAddPhotoButton>
  );
};

export const EditPhotoButton: React.FC<IPhotoButton> = ({ color, onClick }): JSX.Element => {
  return (
    <StyledEditPhotoButton onClick={onClick} textColor={color}>
      <EditIcon size={28} color={color} />
      <span>Edit photo</span>
    </StyledEditPhotoButton>
  );
};

interface IHybridUnderlineControlButton {
  isUnderlineTextHidden: boolean;
  children: string;
  onClick: () => void;
}

export const HybridUnderlineControlButton: React.FC<IHybridUnderlineControlButton> = (props): JSX.Element => {
  const { isUnderlineTextHidden, onClick, children } = props;

  return (
    <StyledHybridUnderlineControlButton
      isUnderlineTextHidden={isUnderlineTextHidden}
      testId="underline-control-button"
      onClick={onClick}
    >
      {children}
    </StyledHybridUnderlineControlButton>
  );
};
